import React, { useEffect } from 'react'
import styles from './Drawer.module.scss'

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  headerActions?: React.ReactNode
  footer?: React.ReactNode
  /** Shown full-width above the footer's buttons, e.g. a save error. */
  footerMessage?: React.ReactNode
  closeOnOverlayClick?: boolean
}

/**
 * Slide-in drawer from the right edge. Mirrors the Modal API for consistency,
 * but gives the body more vertical room for dense forms.
 */
export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  headerActions,
  footer,
  footerMessage,
  closeOnOverlayClick = true,
}) => {
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) onClose()
  }

  return (
    <div className={styles.drawerOverlay} onClick={handleOverlayClick}>
      <div
        className={styles.drawerPanel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <h2>{title}</h2>
          <div className={styles.headerRight}>
            {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className={styles.drawerBody}>{children}</div>

        {footer && (
          <div className={styles.drawerFooter}>
            {footerMessage && <div className={styles.footerMessage}>{footerMessage}</div>}
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export default Drawer
